 import React, { useState } from 'react';
 
import { 
  User, Code, Briefcase, Mail, Menu, X, ExternalLink, Database, 
  Smartphone, Video, Award, Terminal, Server, Shield, Layers, 
  Cpu, CheckCircle2, ChevronRight, Send, MessageSquare
} from 'lucide-react';
// Move import to the top level:
import myPhoto from './assets/yusuf.jpg'; 
 

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('all');

  // --- MACLUUMAADKA ENGR. YUSUF (EXPANDED PORTFOLIO DATA) ---
  const profile = {
    name: "Eng. Yusuf Mahamed Hassan",
    title: "Mobile Application & Full-Stack Developer",
    graduation: "BSc in Computer Science Graduate",
    bio: "I am a highly motivated and detail-oriented Computer Science graduate specializing in building robust mobile applications and scalable full-stack software systems. With deep technical competence in modern frameworks like Flutter and enterprise databases like Supabase, MySQL, and Oracle, I blend functional programming with clean architectural design. Experienced in handling entire development lifecycles from version control workflows to multimedia production, committed to bringing professional excellence and innovative engineering solutions to cross-functional teams.",
    email: "yusuf.mahamed@email.com",
    github: "https://github.com/dameey55", 
    linkedin: "https://linkedin.com", 

    // 1. SKILLS CATEGORIES
    skillCategories: [
      {
        title: "Mobile & Frontend Architecture",
        icon: <Smartphone className="text-teal-400" size={24} />,
        subtitle: "Client-side platforms",
        items: ["Flutter Framework", "Dart Language", "React.js (ES6+)", "Tailwind CSS", "JavaScript", "HTML5 & CSS3", "UI/UX Components"]
      },
      {
        title: "Backend & Database Systems",
        icon: <Database className="text-blue-400" size={24} />,
        subtitle: "Server-side & Storage",
        items: ["Supabase Services", "Relational Databases", "SQL Schema Design", "MySQL", "Oracle Database", "Data Entry Security", "Authentication Integration"]
      },
      {
        title: "Workflows & Multimedia",
        icon: <Video className="text-purple-400" size={24} />,
        subtitle: "Development environment",
        items: ["Android Studio IDE", "Git & GitHub Workflow", "Adobe Premiere Pro", "After Effects", "CapCut Video Production", "Data Analysis (Pandas)", "Excel/CSV Spreadsheets"]
      }
    ],

    // 2. DETAILED PROJECTS
    projects: [
      {
        title: "Automated Fuel Station Management System",
        category: "mobile",
        desc: "A high-end mobile-enabled administrative and logistics platform featuring real-time fuel metrics, interactive configuration rows, and detailed unified modeling language (UML) specifications for optimal station management.",
        tech: ["Flutter", "Dart", "UML Design", "State Tracking"],
        features: ["Real-time fuel metrics", "Admin control panels", "Automated logging"],
        link: "#"
      },
      {
        title: "Mobile-Based Pharmacy Management System",
        category: "fullstack",
        desc: "An enterprise-grade pharmaceutical application designed to automate digital medicine inventories, live sales tracking, and transaction record auditing, integrated with persistent remote storage solutions.",
        tech: ["Flutter", "Supabase", "SQL", "Relational DB"],
        features: ["Medicine inventory tracking", "Sales transactions", "Supabase authentication"],
        link: "#"
      },
      {
        title: "Smart Warehouse Management System",
        category: "mobile",
        desc: "A specialized mobile logistics app focused on end-to-end stock visibility, barcode mock integrations, and real-time inventory adjustments, backed up via collaborative Git repository workflows.",
        tech: ["Flutter", "Dart", "Git/GitHub", "Workspace Architecture"],
        features: ["Logistics tracking", "Inventory audits", "Multi-branch support"],
        link: "#"
      },
      {
        title: "Coffee Shop Commerce Platform",
        category: "ui",
        desc: "A sleek, user-centric mobile application interface that handles smooth menu navigation, favorite list matching logic, interactive detail sheets, and high-fidelity transitions.",
        tech: ["Flutter", "UI/UX Design", "Animations", "Widget Testing"],
        features: ["Favorite matching logic", "Interactive menus", "Smooth transitions"],
        link: "#"
      },
      {
        title: "Responsive Full-Stack Web App",
        category: "fullstack",
        desc: "A modern corporate dashboard constructed using React.js and Tailwind CSS, providing analytics visualization from dynamic datasets for internal organizational evaluation.",
        tech: ["React.js", "Tailwind CSS", "JavaScript", "REST APIs"],
        features: ["Dataframe rendering", "Responsive analytics", "Tailwind styling"],
        link: "#"
      },
      {
        title: "Professional Multimedia Showreel",
        category: "multimedia",
        desc: "A technical post-production editing folder incorporating dynamic velocity clips, professional color grading matrices, and narrative audio design rendered inside the Adobe software suites.",
        tech: ["Premiere Pro", "After Effects", "Audio Drivers", "MP4 Export"],
        features: ["Dynamic transitions", "Color matrices", "High-end rendering"],
        link: "#"
      }
    ],

    // 3. CORE VALUES
    coreValues: [
      { 
        title: "Analytical Engineering", 
        icon: <Cpu className="text-teal-400" size={20} />,
        desc: "Deep capability in tracing runtime application failures, state management bugs, and structural database schema anomalies efficiently." 
      },
      { 
        title: "Cross-Disciplinary Innovation", 
        icon: <Layers className="text-blue-400" size={20} />,
        desc: "Merging algorithmic code structures with high-fidelity visual assets, video production, and pixel-perfect mobile layouts." 
      },
      { 
        title: "Professional Workflow", 
        icon: <Shield className="text-purple-400" size={20} />,
        desc: "Adhering strictly to modern Git branching structures, robust code documentation, and proactive team collaboration milestones." 
      }
    ]
  };

  const filteredProjects = activeTab === 'all' 
    ? profile.projects 
    : profile.projects.filter(p => p.category === activeTab);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 font-sans antialiased selection:bg-teal-500 selection:text-slate-950">
      
      {/* NAVBAR */}
      <nav className="sticky top-0 z-50 bg-slate-950/80 backdrop-blur-md border-b border-slate-900 px-6 py-4">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <a href="#" className="text-xl font-extrabold tracking-wider bg-gradient-to-r from-teal-400 via-cyan-400 to-blue-500 bg-clip-text text-transparent flex items-center gap-2 group">
            <Terminal className="w-5 h-5 text-teal-400 group-hover:rotate-12 transition-transform" /> 
            <span>{profile.name.split(' ')[0]}.Dev</span>
          </a>
          
          <div className="hidden md:flex items-center gap-8 text-sm font-semibold text-slate-400">
            <a href="#about" className="hover:text-teal-400 transition-all">About</a>
            <a href="#skills" className="hover:text-teal-400 transition-all">Expertise</a>
            <a href="#projects" className="hover:text-teal-400 transition-all">Projects</a>
            <a href="#contact" className="hover:text-teal-400 transition-all">Contact</a>
            <a href="#contact" className="px-4 py-2 bg-gradient-to-r from-teal-500 to-blue-600 text-slate-950 rounded-lg hover:shadow-lg hover:shadow-teal-500/20 transition-all">
              Hire Me
            </a>
          </div>

          <button className="md:hidden text-slate-400 hover:text-white" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>

        {isMenuOpen && (
          <div className="md:hidden absolute top-full left-0 w-full bg-slate-900 border-b border-slate-800 px-6 py-6 space-y-4 flex flex-col shadow-2xl">
            <a href="#about" onClick={() => setIsMenuOpen(false)} className="text-lg font-medium text-slate-300 hover:text-teal-400 transition">About Me</a>
            <a href="#skills" onClick={() => setIsMenuOpen(false)} className="text-lg font-medium text-slate-300 hover:text-teal-400 transition">Skills</a>
            <a href="#projects" onClick={() => setIsMenuOpen(false)} className="text-lg font-medium text-slate-300 hover:text-teal-400 transition">Projects</a>
            <a href="#contact" onClick={() => setIsMenuOpen(false)} className="text-lg font-medium text-slate-300 hover:text-teal-400 transition">Contact</a>
            <a href="#contact" onClick={() => setIsMenuOpen(false)} className="w-full py-3 bg-teal-500 text-slate-950 text-center font-bold rounded-xl">Hire Me Now</a>
          </div>
        )}
      </nav>

      {/* HERO SECTION */}
      <header id="about" className="relative overflow-hidden pt-20 pb-20 md:pt-28 md:pb-24 px-6">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl -z-10 animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl -z-10"></div>
        
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center gap-12">
          
          {/* Left Text Block */}
          <div className="flex-1 space-y-6 text-center md:text-left">
            <div className="inline-flex items-center gap-2 bg-slate-900 border border-slate-800 px-4 py-2 rounded-full text-xs font-semibold tracking-wide text-teal-400 shadow-xl mx-auto md:mx-0">
              <Award size={16} className="text-teal-400" />
              <span>{profile.graduation}</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight text-white leading-tight">
              Hi, I am <br />
              <span className="bg-gradient-to-r from-teal-400 via-cyan-400 to-blue-500 bg-clip-text text-transparent">
                {profile.name}
              </span>
            </h1>
            
            <h2 className="text-xl md:text-2xl text-slate-400 font-medium">
              {profile.title}
            </h2>
            
            <p className="text-slate-400 leading-relaxed text-sm md:text-base font-light text-justify bg-slate-900/40 p-6 rounded-2xl border border-slate-900/60 backdrop-blur-xs">
              {profile.bio}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 pt-2">
              <a href="#contact" className="px-6 py-3.5 bg-gradient-to-r from-teal-500 to-blue-600 text-slate-950 font-bold rounded-xl shadow-xl hover:scale-[1.02] transition-all text-center">
                Let's Build Together
              </a>
              <a href="#projects" className="px-6 py-3.5 bg-slate-900 text-slate-200 border border-slate-800 font-bold rounded-xl hover:bg-slate-800 transition-all text-center flex items-center justify-center gap-2">
                <span>Explore Projects</span>
                <ChevronRight size={16} />
              </a>
            </div>
          </div>

          {/* Right Photo Block */}
          <div className="flex-1 flex justify-center md:justify-end w-full">
            <div className="relative group max-w-sm w-full px-4">
              <div className="absolute -inset-1.5 bg-gradient-to-r from-teal-500 to-blue-600 rounded-2xl blur-lg opacity-40 group-hover:opacity-75 transition duration-1000"></div>
              
              <div className="relative bg-slate-900 p-2 rounded-2xl border border-slate-800/80 shadow-2xl overflow-hidden">
                {/* Updated placeholder with actual loaded local image */}
                <div className="w-full h-[450px] bg-slate-950 rounded-xl overflow-hidden flex items-center justify-center border border-slate-800/40">
                  <img 
                    src={myPhoto} 
                    alt={profile.name} 
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => {
                      // Fallback visual in case local asset isn't resolved by bundler
                      e.target.style.display = 'none';
                      e.target.parentNode.innerHTML = `
                        <div class="flex flex-col items-center p-6 text-center text-slate-600">
                          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-user mb-4 text-teal-500"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                          <span class="font-bold text-slate-300 text-sm">Graduation Profile Active</span>
                          <span class="text-xs text-slate-500 mt-2">Rendering asset from source configurations</span>
                        </div>
                      `;
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

        </div>
      </header>

      {/* CORE VALUES */}
      <section className="max-w-6xl mx-auto px-6 py-6">
        <div className="grid md:grid-cols-3 gap-6">
          {profile.coreValues.map((value, idx) => (
            <div key={idx} className="p-6 bg-slate-900/40 backdrop-blur-md rounded-2xl border border-slate-900 flex gap-4">
              <div className="p-3 bg-slate-950 rounded-xl border border-slate-800 h-fit text-teal-400">
                {value.icon}
              </div>
              <div className="space-y-1">
                <h4 className="font-bold text-base text-white">{value.title}</h4>
                <p className="text-xs text-slate-400 leading-relaxed">{value.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* EXPERTISE SECTION */}
      <section id="skills" className="bg-slate-900/30 border-y border-slate-900 py-24 px-6">
        <div className="max-w-6xl mx-auto space-y-16">
          <div className="text-center space-y-3">
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-white">Full-Stack Technical Architecture</h2>
            <p className="text-slate-400 max-w-2xl mx-auto text-sm">
              A comprehensive breakdown of frameworks, languages, and pipeline integration workflows I command.
            </p>
            <div className="w-12 h-1 bg-teal-500 mx-auto rounded-full mt-4"></div>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-8">
            {profile.skillCategories.map((cat, index) => (
              <div key={index} className="bg-slate-900/70 p-8 rounded-2xl border border-slate-800/80 hover:border-slate-700/60 shadow-xl transition-all flex flex-col justify-between group">
                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-slate-950 rounded-xl border border-slate-800 transition-colors shadow-inner">
                      {cat.icon}
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-white tracking-wide">{cat.title}</h3>
                      <p className="text-xs text-slate-500">{cat.subtitle}</p>
                    </div>
                  </div>
                  <div className="h-px bg-gradient-to-r from-slate-800 to-transparent"></div>
                  <div className="flex flex-wrap gap-2.5">
                    {cat.items.map((skill, idx) => (
                      <span key={idx} className="bg-slate-950 text-slate-300 text-xs px-3 py-2 rounded-lg border border-slate-800/80 font-mono tracking-tight hover:border-teal-500/40 hover:text-white transition-colors">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="mt-8 pt-4 border-t border-slate-800/50 flex items-center gap-1.5 text-xs text-teal-400/80 font-medium">
                  <CheckCircle2 size={12} />
                  <span>Verified Operational Proficiency</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROJECTS SECTION */}
      <section id="projects" className="max-w-6xl mx-auto px-6 py-24 space-y-16">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="space-y-3">
            <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-white">Engineering Portfolio & Systems</h2>
            <p className="text-slate-400 max-w-xl text-sm">
              Production-ready mobile software setups, relational database architectures, and media configurations.
            </p>
          </div>
          
          <div className="flex flex-wrap gap-2 bg-slate-900/60 p-1.5 rounded-xl border border-slate-900 w-fit">
            {['all', 'mobile', 'fullstack', 'ui', 'multimedia'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all ${
                  activeTab === tab 
                    ? 'bg-gradient-to-r from-teal-500 to-blue-600 text-slate-950 shadow-md' 
                    : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((proj, index) => (
            <div key={index} className="bg-slate-900/40 p-6 rounded-2xl border border-slate-900 hover:border-slate-800/80 shadow-md hover:-translate-y-1 transition-all flex flex-col justify-between group">
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-[10px] font-extrabold uppercase tracking-widest text-teal-400 bg-teal-500/10 px-2.5 py-1 rounded-md border border-teal-500/20 font-mono">
                    {proj.category}
                  </span>
                  <Briefcase size={14} className="text-slate-600" />
                </div>
                <h3 className="text-xl font-bold text-white tracking-tight group-hover:text-teal-400 transition-colors">
                  {proj.title}
                </h3>
                <p className="text-slate-400 text-xs leading-relaxed font-light text-justify">
                  {proj.desc}
                </p>
                <div className="space-y-1.5 pt-2">
                  {proj.features.map((feat, fIdx) => (
                    <div key={fIdx} className="flex items-center gap-2 text-xs text-slate-400">
                      <span className="w-1 h-1 rounded-full bg-teal-400"></span>
                      <span>{feat}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-4 pt-6">
                <div className="flex flex-wrap gap-1.5">
                  {proj.tech.map((t, idx) => (
                    <span key={idx} className="bg-slate-950 text-slate-400 text-[10px] font-mono px-2 py-1 rounded border border-slate-850">
                      {t}
                    </span>
                  ))}
                </div>
                <div className="h-px bg-slate-800/60"></div>
                <a href={proj.link} className="inline-flex items-center text-xs font-bold text-teal-400 hover:text-teal-300 transition gap-1 group/btn">
                  <span>Explore Repository Modules</span>
                  <ExternalLink size={12} className="group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5 transition-transform" />
                </a>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CONTACT SECTION */}
      <section id="contact" className="bg-slate-900/40 border-t border-slate-900 py-24 px-6">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-5 gap-12 items-center">
          
          <div className="lg:col-span-2 space-y-6">
            <div className="w-12 h-12 bg-teal-500/10 border border-teal-500/20 text-teal-400 rounded-xl flex items-center justify-center shadow-xl">
              <MessageSquare size={24} />
            </div>
            <h2 className="text-3xl font-extrabold tracking-tight text-white">Let's Connect & Collaborate</h2>
            <p className="text-slate-400 text-sm leading-relaxed font-light">
              Whether you are looking to secure a full-time software developer, establish dynamic database integrations, or configure automated state architectures, my terminal is open.
            </p>
            
            <div className="space-y-4 pt-4">
              <div className="bg-slate-950 p-4 rounded-xl border border-slate-900 flex items-center gap-4">
                <Mail className="text-teal-400 w-5 h-5 flex-shrink-0" />
                <div className="overflow-hidden">
                  <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">Direct Email Address</p>
                  <a href={`mailto:${profile.email}`} className="text-sm text-slate-200 hover:text-teal-400 transition font-mono block truncate">
                    {profile.email}
                  </a>
                </div>
              </div>
            </div>

            <div className="flex gap-4 pt-2">
              <a href={profile.github} target="_blank" rel="noreferrer" className="w-10 h-10 bg-slate-950 rounded-xl border border-slate-900 flex items-center justify-center text-slate-400 hover:text-white transition-all shadow-md">
                <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 16 16" height="18" width="18" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.012 8.012 0 0 0 16 8c0-4.42-3.58-8-8-8z"></path>
                </svg>
              </a>
              <a href={profile.linkedin} target="_blank" rel="noreferrer" className="w-10 h-10 bg-slate-950 rounded-xl border border-slate-900 flex items-center justify-center text-slate-400 hover:text-white transition-all shadow-md">
                <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 16 16" height="18" width="18" xmlns="http://www.w3.org/2000/svg">
                  <path d="M0 1.146C0 .513.526 0 1.175 0h13.65C15.474 0 16 .513 16 1.146v13.708c0 .633-.526 1.146-1.175 1.146H1.175C.526 16 0 15.487 0 14.854V1.146zm4.943 12.248V6.169H2.542v7.225h2.401zm-1.2-8.212c.837 0 1.358-.554 1.358-1.248-.015-.709-.52-1.248-1.342-1.248-.822 0-1.359.54-1.359 1.248 0 .694.521 1.248 1.327 1.248h.016zm4.908 8.212V9.359c0-.216.016-.432.08-.586.173-.431.568-.878 1.232-.878.869 0 1.216.662 1.216 1.634v3.865h2.401V9.25c0-2.22-1.184-3.252-2.764-3.252-1.274 0-1.845.7-2.165 1.193v.025h-.016a5.54 5.54 0 0 1 .016-.025V6.169h-2.4c.03.678 0 7.225 0 7.225h2.4z"></path>
                </svg>
              </a>
            </div>
          </div>

          <div className="lg:col-span-3 bg-slate-900 p-8 rounded-2xl border border-slate-800 shadow-2xl relative overflow-hidden">
            <form onSubmit={(e) => e.preventDefault()} className="space-y-5 relative z-10">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wide">Your Name</label>
                  <input type="text" placeholder="John Doe" className="w-full bg-slate-950 border border-slate-800/80 rounded-xl px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-teal-500 transition-colors" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-slate-400 uppercase tracking-wide">Email Address</label>
                  <input type="email" placeholder="john@example.com" className="w-full bg-slate-950 border border-slate-800/80 rounded-xl px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-teal-500 transition-colors" />
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wide">Subject Requirement</label>
                <input type="text" placeholder="Mobile App Project" className="w-full bg-slate-950 border border-slate-800/80 rounded-xl px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-teal-500 transition-colors" />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wide">Detailed Message</label>
                <textarea rows={4} placeholder="Outline the scope of the infrastructure..." className="w-full bg-slate-950 border border-slate-800/80 rounded-xl px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-teal-500 transition-colors resize-none"></textarea>
              </div>
              <button type="submit" className="w-full py-4 bg-gradient-to-r from-teal-500 to-blue-600 text-slate-950 font-extrabold rounded-xl shadow-lg hover:scale-[1.01] transition-all flex items-center justify-center gap-2 tracking-wide uppercase text-xs">
                <span>Dispatch Secure Message</span>
                <Send size={14} />
              </button>
            </form>
          </div>

        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-slate-950 text-slate-600 text-xs text-center py-8 border-t border-slate-900/60 font-mono tracking-tight">
        <div className="max-w-5xl mx-auto px-4 space-y-2">
          <p>© {new Date().getFullYear()} {profile.name}. All Engineering Rights Reserved.</p>
          <p className="text-[10px] text-slate-700">Compiled using React.js & Tailwind CSS Architecture.</p>
        </div>
      </footer>
    </div>
  );
}

export default App;